import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Dashboard } from 'types/dashboard';
import { DashboardWidget } from 'types/dashboard_widget';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useDashboards } from 'dashboards/hooks/useDashboards';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';
import { useModal } from 'hooks/useModal';

import * as Dialog from 'components/Dialog';
import { Button } from 'components/Button/Button';
import { Field } from 'components/Field/Field';
import { FormErrorMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import { Select } from 'components/Select/Select';

type CopyWidgetToDashboardFieldValues = {
  targetDashboardId: Dashboard['id'] | null;
};

type CopyWidgetToDashboardDialogProps = {
  widget: DashboardWidget;
  currentDashboardId: Dashboard['id'];
} & Pick<Dialog.RootProps, 'open'>;

export function CopyWidgetToDashboardDialog({
  open,
  widget,
  currentDashboardId,
}: CopyWidgetToDashboardDialogProps) {
  const { close } = useModal();

  return (
    <Dialog.Root
      open={open}
      className="min-w-md"
      onOpenChange={(open) => !open && close()}
    >
      <Dialog.Title asChild>
        <Heading level="2">Copy widget to dashboard</Heading>
      </Dialog.Title>
      <CopyWidgetToDashboardDialogContent
        widget={widget}
        currentDashboardId={currentDashboardId}
      />
    </Dialog.Root>
  );
}

function CopyWidgetToDashboardDialogContent({
  widget,
  currentDashboardId,
}: Omit<CopyWidgetToDashboardDialogProps, 'open'>) {
  const { close } = useModal();
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<CopyWidgetToDashboardFieldValues>({
    defaultValues: {
      targetDashboardId: null,
    },
  });

  // Fetch all dashboards
  const { data: dashboardsData, isLoading: isLoadingDashboards } =
    useDashboards({ limit: 100 });

  // Fetch target dashboard when selected
  const [selectedDashboardId, setSelectedDashboardId] = useState<
    Dashboard['id'] | null
  >(null);
  const { data: targetDashboard } = useDashboard(selectedDashboardId || '');

  const { mutate: updateDashboard, isLoading: isUpdating } = useUpdateDashboard(
    {
      onSuccess() {
        close();
      },
      onError() {
        setError('Failed to copy widget to dashboard.');
      },
    }
  );

  function onSubmit({ targetDashboardId }: CopyWidgetToDashboardFieldValues) {
    if (!targetDashboardId || !targetDashboard) {
      return;
    }

    // Clone the widget
    const copiedWidget = structuredClone(widget);
    const newId = crypto.randomUUID();
    copiedWidget.id = newId;

    // Get new layout position in target dashboard
    const newLayout = getNewWidgetLayout(
      targetDashboard.configuration.widgets,
      copiedWidget.layout.w,
      copiedWidget.layout.h
    );
    copiedWidget.layout = { ...newLayout, i: newId };

    // Update the target dashboard
    const updatedDashboard = upsertWidget(targetDashboard, copiedWidget);

    updateDashboard({
      id: targetDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  // Filter out current dashboard and create options
  const dashboardOptions =
    dashboardsData?.data
      .filter((dashboard) => dashboard.id !== currentDashboardId)
      .map((dashboard) => ({
        label: dashboard.name,
        value: dashboard.id,
      })) || [];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Field label="Select dashboard" error={errors.targetDashboardId} required>
        <Controller
          name="targetDashboardId"
          control={control}
          rules={{ required: 'Please select a dashboard.' }}
          render={({ field: { value, onChange } }) => (
            <Select
              value={
                value
                  ? dashboardOptions.find((opt) => opt.value === value) || null
                  : null
              }
              onChange={(option) => {
                onChange(option ? option.value : null);
                setSelectedDashboardId(option ? option.value : null);
                setError(null);
              }}
              options={dashboardOptions}
              isLoading={isLoadingDashboards}
              placeholder="Choose a dashboard..."
              autoFocus
            />
          )}
        />
      </Field>

      {error && (
        <FormErrorMessage
          error={{ message: error, code: 'unknown', status: 500 }}
        />
      )}

      <div className="flex justify-end gap-2">
        <Dialog.Close asChild>
          <Button variant="secondary">Cancel</Button>
        </Dialog.Close>
        <Button variant="primary" type="submit" loading={isUpdating}>
          Copy
        </Button>
      </div>
    </form>
  );
}
